import { useState, useRef, useCallback } from 'react'
import { Upload, FileText, CheckCircle, AlertCircle, Loader2, X } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { documentService } from '@/services'
import { useLegacyUpdatesStream } from '@/hooks/useLegacyUpdatesStream'
import type { LegacyUpdateEvent } from '@/hooks/useLegacyUpdatesStream'

interface DocumentUploadViewProps {
  onComplete: () => void
}

type UploadState = 'idle' | 'uploading' | 'processing' | 'completed' | 'error'

const ACCEPTED_EXTENSIONS: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'text/plain': 'text',
}

const ACCEPT_STRING = Object.keys(ACCEPTED_EXTENSIONS).join(',') + ',.pdf,.pptx,.txt'

function resolveDocumentType(file: File): string {
  const mimeType = ACCEPTED_EXTENSIONS[file.type]
  if (mimeType) return mimeType

  const ext = file.name.split('.').pop()?.toLowerCase()
  if (ext === 'pdf') return 'pdf'
  if (ext === 'pptx') return 'pptx'
  if (ext === 'txt') return 'text'

  return 'pdf'
}

export function DocumentUploadView({ onComplete }: DocumentUploadViewProps) {
  const [state, setState] = useState<UploadState>('idle')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [documentId, setDocumentId] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const completionTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Listen for SSE events once we have a documentId and are in processing state
  useLegacyUpdatesStream({
    entityTypes: ['document'],
    entityIds: documentId ? [documentId] : undefined,
    enabled: state === 'processing' && !!documentId,
    onUpdate: useCallback(
      (event: LegacyUpdateEvent) => {
        if (event.entityId !== documentId) return

        if (event.status === 'completed') {
          setState('completed')
          completionTimer.current = setTimeout(onComplete, 1500)
        } else if (event.status === 'failed') {
          setState('error')
          setError('Document processing failed. Please try again.')
        }
      },
      [documentId, onComplete]
    ),
  })

  const handleFileSelect = (file: File) => {
    setSelectedFile(file)
    setError(null)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFileSelect(file)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFileSelect(file)
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    setState('uploading')
    setError(null)

    try {
      const type = resolveDocumentType(selectedFile)
      const res = await documentService.uploadDocument(selectedFile, type)

      if (res.documentId) {
        // Set documentId first, then transition to processing state
        // This ensures the documentId is available when useLegacyUpdatesStream enables
        setDocumentId(res.documentId)
        // Use setTimeout to ensure documentId state update completes first
        setTimeout(() => setState('processing'), 0)
      } else {
        // No documentId means processing was synchronous or something unexpected
        setState('completed')
        completionTimer.current = setTimeout(onComplete, 1500)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed. Please try again.'
      setError(message)
      setState('error')
    }
  }

  const handleRetry = () => {
    setState('idle')
    setError(null)
    setDocumentId(null)
    setSelectedFile(null)
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    setError(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return (
    <Card className="flex flex-col border-none bg-card/75 backdrop-blur-sm shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-white/5 bg-black/20">
        <h2 className="text-lg font-semibold text-white mb-1">
          Upload a Document
        </h2>
        <p className="text-xs text-muted-foreground">
          Upload a pitch deck, business plan, or any document about your startup.
          We'll extract key context automatically.
        </p>
      </div>

      {/* Content */}
      <div className="p-6 flex-1">
        {state === 'idle' && (
          <div className="space-y-4">
            {/* Drop zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all',
                isDragging
                  ? 'border-primary bg-primary/10'
                  : 'border-white/10 hover:border-primary/40 hover:bg-white/5'
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_STRING}
                onChange={handleInputChange}
                className="hidden"
                title="Upload a document file"
                aria-label="Upload a document file"
              />
              <Upload className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm text-foreground font-medium">
                Drop a file here or click to browse
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                PDF, PPTX, or TXT
              </p>
            </div>

            {/* Selected file */}
            {selectedFile && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 border border-white/5">
                <FileText className="h-5 w-5 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">
                    {selectedFile.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveFile()
                  }}
                  className="p-1 text-muted-foreground hover:text-white transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {error && (
              <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <Button
              className="w-full h-12 text-base bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20"
              onClick={handleUpload}
              disabled={!selectedFile}
            >
              Upload & Process
            </Button>
          </div>
        )}

        {state === 'uploading' && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <Loader2 className="h-10 w-10 text-primary animate-spin" />
            <p className="text-sm text-foreground font-medium">Uploading document...</p>
            <p className="text-xs text-muted-foreground">
              {selectedFile?.name}
            </p>
          </div>
        )}

        {state === 'processing' && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <Loader2 className="h-10 w-10 text-primary animate-spin" />
            <p className="text-sm text-foreground font-medium">
              Processing your document...
            </p>
            <p className="text-xs text-muted-foreground text-center">
              Extracting entities, classifying content, and building your context graph.
              This may take a moment.
            </p>
          </div>
        )}

        {state === 'completed' && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <CheckCircle className="h-10 w-10 text-green-400" />
            <p className="text-sm text-foreground font-medium">
              Document processed successfully!
            </p>
            <p className="text-xs text-muted-foreground">
              Proceeding to next step...
            </p>
          </div>
        )}

        {state === 'error' && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <AlertCircle className="h-10 w-10 text-destructive" />
            <p className="text-sm text-destructive text-center">{error}</p>
            <Button
              variant="ghost"
              onClick={handleRetry}
              className="hover:bg-secondary hover:text-white"
            >
              Try again
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
}
